import React, {useContext, useEffect, useState} from "react";
import {useAuth0} from "@auth0/auth0-react";
import {customerFields} from "../settings";
import {fetchCustomer, fetchCustomerAccounts} from "../apiRequests/api";
import Selects from "./Selects";
import {SigninContext} from "../contexts/SigninContext";
import '../sass/header/profile.scss';
import * as Modules from "./Modules";
import Popup from "reactjs-popup";
import {NavLink} from "react-router-dom";
import {useAppSettings} from '../hooks/useAppSettings';
import {Link} from "react-router-dom";
import {useRrpCalculatorVisibility} from "../hooks/useRrpCalculatorVisibility";

function Profile() {
    const settings = useAppSettings();
    const {
        userName,
        setUserName,
        customerName,
        setCustomerName,
        setDisplayProfile,
        originalUserName,
        originalCustomerName,
        switchToOriginalUser
    } = useContext(SigninContext);
    
    // Debug log when customerName changes
    useEffect(() => {
    }, [customerName, userName]);
    
    const [customer, setCustomer] = useState([]);
    const [selectsLoading, setSelectsLoading] = useState(true);
    const [vintnerError, setVintnerError] = useState(false);
    const [filterStates, setFilterStates] = useState(customerFields.map(el => ''));
    const [filterInfo, setFilterInfo] = useState(customerFields.map(el => el));
    const { getIdTokenClaims, logout } = useAuth0();
    const [rrpCalculatorVisible, toggleRrpCalculatorVisible] = useRrpCalculatorVisibility();
    
    let fetchParameters = {
        "CustomerAccount": userName
    };

    function logoutUser() {
        logout({returnTo: window.location.origin})
    }

    // we need to request options first from api to be rendered for search selects
    async function loadOptions() {
        const token = await getIdTokenClaims()
        const data = await fetchCustomerAccounts(token.__raw)
        if (!data.error) {
            setFilterInfo(filterInfo.map(state => {
                if (state.select) {
                    state.options = data.mainResult[state.name]
                    // Check if saved userName exists in options fetched from backend.
                    const validOption = state.options.find(option => option.value === userName)

                    if (!userName || !validOption) {
                      setUserName(state.options[0].value)
                    }
                    return state
                } else {

                    return state
                }
            }))
            setSelectsLoading(false)
        } else if (data.error === 'email_not_found') {
            alert('Error loading customer data: Login email not found. Please contact us on ' + process.env.REACT_APP_PHONE + ' or email ' + process.env.REACT_APP_EMAIL)
            logoutUser()
        } else {
            setVintnerError(true)
        }
    }

    const loadCustomer = React.useCallback(async () => {
        if (!userName) return;

        const token = await getIdTokenClaims();
        const data1 = await fetchCustomer({ CustomerAccount: userName }, token.__raw);

        if (!data1.error) {
            setCustomer(data1);

            // Update customer name from the response
            const nameFromResponse = data1.CustomerName || data1.Billing_ContactName;

            if (nameFromResponse) {
                setCustomerName(nameFromResponse);
            }
        } else {
            setCustomer({});
        }
    }, [userName, setCustomerName, getIdTokenClaims]);

    function handleFilterChange(val, order) {
        const newArr = filterStates
        newArr[order] = val
        setFilterStates(filterStates.map((el, index) => index + 1 === order ? val : el))
    }

    useEffect(() => {
        loadOptions()
    }, [])
    
    // Reload customer data when userName changes
    useEffect(() => {
        if (userName) {
            loadCustomer()
        }
    }, [userName])

    const isAdmin = settings && settings['__is_admin_user'] === true;

    return (
        <>
        {vintnerError && (
            <div className="vintner-error-banner">
                <i className="fas fa-exclamation-triangle"></i>
                Error connecting to the ordering system. Please try refreshing the page or contact us on <a href={`tel:${process.env.REACT_APP_PHONE}`}>{process.env.REACT_APP_PHONE}</a>.
            </div>
        )}
        <div className="customer-profile">
            <div className={'container'}>
                <div className={"admin"}>
                    {/* Admin Controls - Only shown for admin users */}
                    {isAdmin ? (
                        <div className="app-settings">
                            <Link to='/admin/settings' title="Admin Settings" className="icon-link">
                                <i className="fa fa-cog"></i>
                            </Link>
                            <Link
                                to='/order-for-customer'
                                title="Order for Customer"
                                className="icon-link"
                            >
                                <i className="fas fa-users"></i>
                            </Link>
                            <Link
                                to='/admin/guide'
                                title="Admin Guide"
                                className="icon-link"
                            >
                                <i className="fas fa-question-circle"></i>
                            </Link>

                            {/* Customer Info - Always show */}
                            <div className="current-customer">
                                <div className="viewing-as">
                                    {/* Only show "Viewing as:" label when impersonating */}
                                    {originalUserName && userName !== originalUserName && (
                                        <span className="label">Viewing as:</span>
                                    )}
                                    <span className="customer-name">
                                        {customerName || customer?.Billing_Name || userName}
                                    </span>
                                    <span className="customer-account">
                                        ({userName})
                                    </span>
                                </div>

                                {/* Only show original user info and switch button when impersonating */}
                                {originalUserName && userName !== originalUserName && (
                                    <>
                                        <span className="divider">|</span>
                                        <div className="original-user">
                                            <span className="label">Logged in as:</span>
                                            <span className="original-name">
                                                {originalCustomerName || originalUserName}
                                            </span>
                                        </div>
                                        <button
                                            onClick={switchToOriginalUser}
                                            className="icon-link switch-back-btn"
                                            title="Switch back to your account"
                                        >
                                            <i className="fas fa-house-person-return"></i>
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    ) : (
                        // Non-admin customer dropdown
                        !selectsLoading && (
                            customerFields.map((field, index) => (
                                <Selects 
                                    key={index}
                                    options={filterInfo[index].options} 
                                    setPropsValue={handleFilterChange}
                                    value={userName} 
                                    order={index + 1} 
                                    defaultValue={userName}
                                />
                            ))
                        )
                    )}
                </div>
                <div className='icon-cont'>
                    {process.env.REACT_APP_RRP_CALCULATOR === "1" &&
                        <label className="rrp-calculator-toggle" title="Show the RRP calculator on product pages">
                            <span className="rrp-calculator-toggle__label">RRP Calculator</span>
                            <span className="rrp-calculator-toggle__switch">
                                <input
                                    type="checkbox"
                                    id="rrp-calculator-toggle"
                                    name="rrpCalculatorToggle"
                                    checked={rrpCalculatorVisible}
                                    onChange={toggleRrpCalculatorVisible}
                                />
                                <span className="rrp-calculator-toggle__slider"></span>
                            </span>
                        </label>
                    }
                    {process.env.REACT_APP_MYREP === "1" &&
                        <Popup className="my-rep-container"
                               trigger={<span className='nav-linked-in' title={'Account Manager'}><i
                                   className="fa-2x fa-thin fa-address-card"></i></span>} arrow={false}
                               modal
                               nested>
                            {close => (<>
                                <Modules.Fallback fallback={null}>
                                    <Modules.MyRep/>
                                </Modules.Fallback>
                                <div className='close_modal' onClick={() => close()}>&times;</div>
                            </>)}
                        </Popup>
                    }
                </div>
            </div>

        </div>
        </>
    )
}

export default Profile